// The league hub (SITE_HOME.md, scenarios A4, K1, K2): what each league
// needs from me, then one card per league grouped by sport, then start and
// join. The chores strip is pinned; the cards scroll. Drawn through the kit
// (LOOKS.md Part 7).

import {
  formatInZone,
  racesOnPoints,
  type Chore,
  type LeagueCard as LeagueCardView,
} from '@fsp/domain';
import { Link } from 'react-router-dom';
import { deviceZone, useHub, useHubStatus, useReaderZone } from '../data/hooks';
import { DEMO_PATH } from '../data/demo';
import { useLeagueEntry } from '../data/leagueEntry';
import { useSession } from '../data/session';
import { useSim } from '../data/SimProvider';
import { cardFor, useLeagues } from '../data/LeaguesProvider';
import { useEffect, useMemo, useState } from 'react';
import { storageKey } from '../data/storage';
import { SignUp, START_REASON } from '../demo/SignUp';
import { Cap, EmptyState, LiveDot, Surface, TeamMark } from '../kit';
import { Icon } from '../kit/Icon';
import { Button, Chip, Line, Text, type Tone } from '../kit/ui';
import '../styles/screens/hub.css';
import { ordinal } from '../shell/AppShell';
import { useTier } from '../shell/useTier';
import { getSport } from '../sports/registry';
import { MatchupCard } from './shared/MatchupCard';
import { TwoGamesNote } from './shared/TwoGames';
import { recordText } from './schedule/season';
import { leaguesBySport } from './gameDay/flip';
import { FieldWeekCard } from './gameDay/FieldTable';
import { ImportLeagueSheet } from './shared/ImportLeagueSheet';

/** `tour`: the tour's own hub, which shows the tour's leagues and nothing else (`useHub`). */
export function HubScreen({ tour = false }: { tour?: boolean }) {
  const hub = useHub({ tour });
  const status = useHubStatus();
  const tier = useTier();
  const { session, update } = useSession();
  const columns = tier === 'phone' ? 1 : tier === 'tablet' ? 2 : 3;
  const [bringing, setBringing] = useState(false);

  // A sport's leagues together, as Game Day's flip goes through them (F7).
  const groups = leaguesBySport(hub.leagues);

  // "You are in no leagues" and "we have not asked yet" look identical, and
  // one of them is a lie — so the empty state waits for the answer, and SAYS
  // it is waiting: this returned null until 2026-09-24, and a server that
  // never answered left an empty page with nothing on it saying why. The wait
  // is bounded because every request is (`NO_ANSWER_MS`, B-133), and the
  // failure is shown rather than swallowed into an empty hub.
  if (hub.leagues.length === 0 && status.loading) {
    return (
      <div className="fsp-screen">
        <EmptyState>Loading your leagues…</EmptyState>
      </div>
    );
  }
  if (hub.leagues.length === 0) return <EmptyHub error={status.error} />;

  return (
    <div className="fsp-screen">
      <div className="fsp-pinned">
        {session?.demo ? (
          // What a signed-in person sees: the main thing is create or join,
          // and the demo is a smaller, secondary button.
          // This is the other half of that — once you are IN it, it has to
          // say so and it has to let you out.
          //
          // Both were missing. Nothing on this screen distinguished example
          // leagues from real ones, and it mattered more than it sounds:
          // the seeded fixture was built FROM this simulator, so the example
          // leagues are named identically to the ones testuser really owns.
          // And `demo: false` existed nowhere in the app, so the only way
          // out was signing out.
          <div className="fsp-guest-bar fsp-hub-guest">
            <span>Example leagues, so there is something to look at. Nothing here is saved.</span>
            <Button
              size="xs"
              onClick={() => {
                void update({ demo: false });
              }}
            >
              Leave
            </Button>
          </div>
        ) : null}
        {!session ? (
          // In the tutorial there is no account and nothing here is saved. The
          // chapter card says what to look at; this says where you are.
          <div className="fsp-guest-bar fsp-hub-guest">
            <span>
              A league mid-season, so there is something to look at. Nothing here is saved.
            </span>
            <SignUp size="xs" />
          </div>
        ) : null}
        {/* "All caught up" is a claim, so it is only made when the chore
            list is complete. See `useHubStatus`. */}
        {/* Null is "nobody worked them out" and renders nothing; an empty list
            is a real answer and renders "All caught up." */}
        {/* AN ACCOUNT FROM BEFORE THE ZONE COULD BE CHOSEN (B-181): its times
            read in each league's zone, labeled, until it picks its own. One
            line with the way to fix it, rather than a guess made for them. */}
        {session !== null && session.timeZone === null && !tour ? (
          <div className="fsp-health" role="status">
            <div className="fsp-grow fsp-ui-wrap-text">
              Pick your time zone, and every time on the site shows in yours.
            </div>
            <Button size="xs" to="/account">
              Set time zone
            </Button>
          </div>
        ) : null}
        {/* With no league there is nothing to be caught up on, and saying so
            above "no leagues yet" is a claim about nothing. */}
        {hub.chores !== null && hub.leagues.length > 0 ? <ChoresStrip chores={hub.chores} /> : null}
      </div>
      {/* max-content rows (hub.css): a definite-height grid would otherwise
          shrink the overflow-hidden cards to fit instead of scrolling. */}
      <div className={`fsp-widgets fsp-widgets-${columns} fsp-hub-grid`}>
        {[...groups.entries()].map(([sport, leagues]) => (
          <SportGroup key={sport} sport={sport} leagues={leagues} />
        ))}
        {/* Owning a league needs an account, so a visitor is offered one
            instead of a button the route guard would bounce. */}
        <div className="fsp-hub-actions fsp-hub-span">
          {session ? (
            <>
              <Button variant="primary" icon="add" to="/leagues/new">
                Start a league
              </Button>
              <Button icon="ticket" to="/join">
                Join with a code
              </Button>
              {/* A LEAGUE FROM ANOTHER SITE, OR THIS ONE (A13, H10). */}
              <Button variant="quiet" icon="upload" onClick={() => setBringing(true)}>
                Bring a league
              </Button>
              <ImportLeagueSheet opened={bringing} onClose={() => setBringing(false)} />
            </>
          ) : (
            <SignUp label="Create an account to start a league" reason={START_REASON} />
          )}
        </div>
      </div>
    </div>
  );
}

/** Zero leagues (SITE_HOME.md, scenario A4): inviting, three doors, no blank page. */
function EmptyHub({ error }: { error: string | null }) {
  const { session } = useSession();
  const { reload } = useLeagues();
  const [bringing, setBringing] = useState(false);
  return (
    <div className="fsp-screen">
      <div className="fsp-widgets fsp-hub-empty">
        <div className="fsp-empty-hub">
          <div className="fsp-disp fsp-hub-welcome">
            Welcome, {session?.displayName ?? 'friend'}.
          </div>
          {error ? (
            // Fail loudly: a hub that could not load is not an empty hub.
            // Telling somebody they have no leagues when we simply could not
            // ask is the silent fallback the house rules forbid.
            <>
              <Text tone="bad" size="md" block center>
                {error}
              </Text>
              <Button size="xs" onClick={reload}>
                Try again
              </Button>
            </>
          ) : (
            <Text tone="secondary" size="md" block center>
              Nothing here yet. Start a league and send the link to your friends, or join one with
              the code somebody sent you.
            </Text>
          )}
          <div className="fsp-hub-actions fsp-hub-actions-center">
            <Button variant="primary" icon="add" to="/leagues/new">
              Start a league
            </Button>
            <Button icon="ticket" to="/join">
              Join with a code
            </Button>
          </div>
          <Button variant="quiet" size="xs" icon="upload" onClick={() => setBringing(true)}>
            Bring a league from a file
          </Button>
          <ImportLeagueSheet opened={bringing} onClose={() => setBringing(false)} />
          {/* Deliberately smaller than the two above. After signing in,
              Create/Join a league is the main option, since that is why
              people are here; the tutorial/demo stays available as a
              smaller button. Somebody who has just made an account came to
              play with their friends; looking around is the fallback, not the
              offer. */}
          <Button variant="quiet" size="xs" icon="play" to={DEMO_PATH}>
            Look around the demo league
          </Button>
        </div>
      </div>
    </div>
  );
}

function SportGroup({ sport, leagues }: { sport: string; leagues: LeagueCardView[] }) {
  const def = getSport(sport as never);
  return (
    <>
      <div className="fsp-hub-span">
        <Cap>
          {def.name} · {leagues.length} league{leagues.length === 1 ? '' : 's'}
        </Cap>
      </div>
      {leagues.map((l) => (
        <LeagueCard key={l.league.id} card={l} />
      ))}
    </>
  );
}

function LeagueCard({ card }: { card: LeagueCardView }) {
  const { league, myTeam } = card;
  // Where this person chose the league opens (B-247); My Team unless they did.
  const home = useLeagueEntry()(league.id, league.status === 'setup');
  const chipZone = useReaderZone() ?? deviceZone();
  return (
    <Surface className="fsp-league-card">
      <Link to={home} className="fsp-league-card-head">
        {myTeam ? <TeamMark team={myTeam} size={36} /> : null}
        <div className="fsp-grow">
          <div className="fsp-league-card-name">
            <span className="fsp-name-text">{league.name}</span>
            {card.live ? <LiveDot /> : null}
          </div>
          <div className="fsp-sub">
            {myTeam?.name ?? 'No team yet'}
            {league.isCommissioner
              ? ' · you run this one'
              : league.needsCommissioner
                ? ' · needs a commissioner'
                : ` · ${league.commissionerName} runs it`}
          </div>
        </div>
        {league.isCommissioner ? <Chip tone="accent">Commish</Chip> : null}
      </Link>

      <div className="fsp-league-card-body">
        {league.status === 'setup' ? (
          <DraftCountdown card={card} />
        ) : card.matchup && card.secondMatchup ? (
          // TWO OPPONENTS A WEEK (Q6, B-614): both games, and what they are.
          <div className="fsp-two-games">
            <MatchupCard
              m={card.matchup}
              compact
              leagueId={league.id}
              stats={card.stats ?? undefined}
            />
            <MatchupCard
              m={card.secondMatchup}
              compact
              leagueId={league.id}
              stats={card.stats ?? undefined}
            />
            <TwoGamesNote games={[card.matchup, card.secondMatchup]} />
          </div>
        ) : card.matchup ? (
          <MatchupCard
            m={card.matchup}
            compact
            leagueId={league.id}
            stats={card.stats ?? undefined}
          />
        ) : card.field && card.fieldOf !== null ? (
          // A league with no opponents: my week against the field (Q4, Q5).
          <FieldWeekCard
            row={card.field}
            of={card.fieldOf}
            allPlay={league.format.id === 'all-play'}
            leagueId={league.id}
          />
        ) : card.eliminatedPeriod !== null ? (
          // Knocked out of an elimination league (Q1, B-364): said before the
          // door below, which a season being played otherwise takes, and
          // which offered "Week 6's matchup" in a league with no matchups.
          <Text tone="secondary">Out after Week {card.eliminatedPeriod}.</Text>
        ) : // NULL IN A SEASON BEING PLAYED is a week whose game the server
        // could not name for this team — a bye, or a bracket slot with no
        // seed yet (K1, ICD-S01 §15) — and the matchup screen says which. It
        // was a door like this one while the hub served no matchup at all.
        card.period && (league.status === 'in-season' || league.status === 'playoffs') ? (
          <Link to={`/l/${league.id}/matchup`} className="fsp-league-card-matchup">
            {card.period.label}’s matchup <Icon name="next" size="sm" />
          </Link>
        ) : league.status === 'drafting' ? (
          <Text tone="secondary">No games until the draft is done.</Text>
        ) : (
          <Text tone="secondary">No game this week.</Text>
        )}
      </div>

      <Link to={home} className="fsp-league-card-foot">
        <span className="fsp-sub">
          {card.standing && myTeam
            ? // Ties third when there are any, as the matchup above prints the record.
              // A race on points has no record to print (Q5): its place alone.
              racesOnPoints(league.format.id)
              ? `${ordinal(card.standing.rank)} of ${card.standing.of}`
              : `${myTeam.record.wins}–${myTeam.record.losses}${myTeam.record.ties ? `–${myTeam.record.ties}` : ''} · ${ordinal(card.standing.rank)} of ${card.standing.of}`
            : `${card.members.joined} of ${card.members.of} in`}
          {card.period ? ` · ${card.period.label}` : ''}
        </span>
        <span className="fsp-hub-chips">
          {(card.chores ?? []).map((c, i) => (
            <Chip key={i} tone={choreTone(c)}>
              {choreChip(c, chipZone)}
            </Chip>
          ))}
        </span>
      </Link>
    </Surface>
  );
}

/** The pre-draft body of a card: when, who is in, and the room when it opens. */
function DraftCountdown({ card }: { card: LeagueCardView }) {
  const { now: simNow } = useSim();
  const zone = useReaderZone() ?? deviceZone();
  // A REAL LEAGUE'S DRAFT IS ON THE WALL CLOCK, in the reader's own zone. The
  // card read the simulator's clock (a fixed Sunday afternoon) and printed New
  // York time for every league, so a real draft at 2 a.m. Mountain read
  // "Opening now" at "4:00 AM" the evening before (found 2026-09-24).
  const real = cardFor(useLeagues().snapshot, card.league.id) !== null;
  const [wall, setWall] = useState(() => Date.now());
  useEffect(() => {
    if (!real) return;
    const id = window.setInterval(() => setWall(Date.now()), 60_000);
    return () => window.clearInterval(id);
  }, [real]);
  const now = real ? wall : simNow;
  const draft = card.chores?.find((c) => c.kind === 'draft');
  const at = card.league.draftAt ? Date.parse(card.league.draftAt) : null;
  return (
    <div className="fsp-draft-countdown">
      <div className="fsp-disp fsp-hub-when">
        {at ? `Draft ${when(at, zone)}` : 'Draft not scheduled'}
      </div>
      <div className="fsp-sub">
        {draft?.roomOpen
          ? 'Draft room is open.'
          : at
            ? `${opensIn(at, now)} · ${card.members.joined} of ${card.members.of} in`
            : `${card.members.joined} of ${card.members.of} in`}
      </div>
      {real ? (
        // A REAL LEAGUE'S DRAFT, ITS DOOR AND ITS LIST. The room since D10
        // for a league that drafts live, and the order and then the board
        // for one that drafts by itself; the same address serves both. It
        // offered only the priorities, as if no league had a room to join
        // (the audit of 2026-10-17).
        <Line gap={2} wrap>
          <Button size="xs" to={`/l/${card.league.id}/draft`}>
            Draft room
          </Button>
          {card.myTeam ? (
            <Button size="xs" variant="quiet" to={`/l/${card.league.id}/priorities`}>
              Draft priorities
            </Button>
          ) : null}
        </Line>
      ) : (
        <Button
          size="xs"
          variant={draft?.roomOpen ? 'primary' : 'secondary'}
          to={`/l/${card.league.id}/draft`}
        >
          {draft?.roomOpen ? 'Join draft room' : 'Draft room'}
        </Button>
      )}
    </div>
  );
}

/**
 * THE FIRST TIME A KIND OF CHORE COMES ROUND (DIRECTION A2, B-445), its line
 * says in one sentence what it is: the week's calendar for somebody who has
 * never played, as each part of it arrives. Once per kind for this person on
 * this device, kept in the browser like the other things put away. Words, no
 * numbers, so every detail level shows them.
 */
const EXPLAINED_KEY = storageKey('chores.explained.v1');
const FIRST_TIME: Record<Chore['kind'], string> = {
  draft:
    'The draft is when every team is picked, player by player. It happens at this time whether or not you are there.',
  lineup:
    'Something on this team needs a fix before the games: a starting slot that scores nothing (empty, on a bye or ruled Out), or a roster the rules do not allow.',
  'trade-offer':
    'Another manager has offered you a trade. Nothing changes unless you accept it before it runs out.',
  result:
    'Each week your starters’ points are added up against your opponent’s, and the higher total wins.',
  'waiver-pending':
    'Claims on players nobody has are settled together at that time. When two teams claim the same player, the league’s waiver rules decide who gets him.',
};

function readExplained(): Set<string> {
  try {
    return new Set(JSON.parse(localStorage.getItem(EXPLAINED_KEY) ?? '[]') as string[]);
  } catch {
    return new Set();
  }
}

/** The kinds to explain on this visit: those this device had not seen when the hub opened. */
function useFirstTime(chores: readonly Chore[]): Set<Chore['kind']> {
  const [seenBefore] = useState(readExplained);
  const kinds = useMemo(() => [...new Set(chores.map((c) => c.kind))], [chores]);
  useEffect(() => {
    if (kinds.every((k) => seenBefore.has(k))) return;
    try {
      const now = readExplained();
      for (const k of kinds) now.add(k);
      localStorage.setItem(EXPLAINED_KEY, JSON.stringify([...now]));
    } catch {
      // Storage blocked: the sentence comes back next visit, which is harmless.
    }
  }, [kinds, seenBefore]);
  return useMemo(() => new Set(kinds.filter((k) => !seenBefore.has(k))), [kinds, seenBefore]);
}

function ChoresStrip({ chores }: { chores: Chore[] }) {
  const zone = useReaderZone() ?? deviceZone();
  const explain = useFirstTime(chores);
  if (chores.length === 0) {
    return (
      <div className="fsp-health fsp-health-ok" role="status">
        <span className="fsp-health-mark" data-status="ok">
          <Icon name="accept" />
        </span>
        <Text weight="strong">All caught up. Enjoy the week.</Text>
      </div>
    );
  }
  return (
    <div className="fsp-chores" role="list">
      {chores.map((c, i) => (
        <div key={i} className={`fsp-chore fsp-chore-${c.kind}`} role="listitem">
          <div className="fsp-grow">
            <Cap>{c.leagueName}</Cap>
            <div className="fsp-chore-text">{choreText(c, zone)}</div>
            {explain.has(c.kind) && chores.findIndex((x) => x.kind === c.kind) === i ? (
              <Text size="xs" tone="secondary" block>
                {FIRST_TIME[c.kind]}
              </Text>
            ) : null}
          </div>
          <ChoreAction chore={c} />
        </div>
      ))}
    </div>
  );
}

function ChoreAction({ chore }: { chore: Chore }) {
  const base = `/l/${chore.leagueId}`;
  switch (chore.kind) {
    case 'lineup':
      // Straight to the slot that cannot score, with it already picked out.
      // One tap gets you to the decision; the decision stays yours.
      return chore.swap ? (
        <Button
          size="xs"
          variant="caution"
          to={`${base}?slot=${chore.swap.rowSlot}&i=${chore.swap.rowSlotIndex}`}
        >
          Open {chore.swap.rowSlot}
        </Button>
      ) : (
        <Button size="xs" to={base}>
          Open lineup
        </Button>
      );
    case 'draft':
      return (
        <Button
          size="xs"
          variant={chore.roomOpen ? 'primary' : 'secondary'}
          to={chore.roomOpen ? `${base}/draft` : base}
        >
          {chore.roomOpen ? 'Join draft room' : 'Open league'}
        </Button>
      );
    case 'trade-offer':
      // THE OFFER ITSELF (B-418), open on Trades, not the league's front door.
      return (
        <Button size="xs" to={`${base}/trades?trade=${encodeURIComponent(chore.tradeId)}`}>
          See the offer
        </Button>
      );
    case 'result':
      return (
        <Button
          size="xs"
          variant={chore.won ? 'primary' : 'secondary'}
          to={`${base}/matchup/${chore.matchupId}`}
        >
          See it
        </Button>
      );
    case 'waiver-pending':
      // MY CLAIMS (B-418), where they can be read, reordered or taken back.
      return (
        <Button size="xs" to={`${base}/waivers`}>
          My claims
        </Button>
      );
  }
}

/** Days to go before a draft's time is said by its date rather than its weekday (B-418). */
const DRAFT_DATE_AFTER_DAYS = 6;

/**
 * A draft's time (B-418): the weekday within the week ahead, as "Sun 7:00 PM"
 * reads; further off, the date, since a weekday a month away names no day.
 */
function draftWhen(ms: number, zone: string): string {
  return ms - Date.now() > DRAFT_DATE_AFTER_DAYS * 86_400_000
    ? formatInZone(ms, zone, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
    : when(ms, zone);
}

function choreText(c: Chore, zone: string): string {
  switch (c.kind) {
    case 'lineup':
      return c.headline;
    case 'draft':
      return c.roomOpen
        ? `Draft room is open. ${c.joined} of ${c.of} in.`
        : `Draft ${draftWhen(Date.parse(c.startsAt), zone)} · ${c.joined} of ${c.of} in`;
    case 'trade-offer':
      return `${c.fromOwner} wants to trade: ${c.summary}`;
    case 'result':
      // A WEEK OF TWO GAMES (Q6): each game in a few words.
      if (c.second !== null) {
        const said = (g: { won: boolean; tied: boolean; margin: number; opponentName: string }) =>
          g.tied
            ? `tied with ${g.opponentName}`
            : g.won
              ? `beat ${g.opponentName}${g.margin < 0.05 ? ' on the tiebreak' : ` by ${g.margin.toFixed(1)}`}`
              : `lost to ${g.opponentName}${g.margin < 0.05 ? ' on the tiebreak' : ` by ${g.margin.toFixed(1)}`}`;
        return `Final: you ${said(c)}, and ${said(c.second)}.`;
      }
      // A LEVEL SCORE is a tie, or a game the league's tiebreak decided.
      if (c.tied) return `Final: tied with ${c.opponentName}.`;
      if (c.margin < 0.05)
        return c.won
          ? `Final: you won on the tiebreak.`
          : `Final: lost to ${c.opponentName} on the tiebreak.`;
      return c.won
        ? `Final: you won by ${c.margin.toFixed(1)}. Go tell someone.`
        : // No "Next week." — a team knocked out has none.
          `Final: lost by ${c.margin.toFixed(1)} to ${c.opponentName}.`;
    case 'waiver-pending':
      // WHEN THEY ARE DECIDED, in the reader's zone (§8 of the plan).
      return `${c.count} waiver claim${c.count === 1 ? '' : 's'}, decided ${when(Date.parse(c.processesAt), zone)}`;
  }
}

function choreChip(c: Chore, zone: string): string {
  switch (c.kind) {
    case 'lineup':
      return c.problemCount === 1 ? '1 lineup problem' : `${c.problemCount} lineup problems`;
    case 'draft': {
      if (c.roomOpen) return 'Draft room open';
      // A DRAFT FAR OFF SAYS WHEN (B-418): "Draft soon" a month ahead was not.
      const at = Date.parse(c.startsAt);
      return at - Date.now() > DRAFT_DATE_AFTER_DAYS * 86_400_000
        ? `Draft ${formatInZone(at, zone, { month: 'short', day: 'numeric' })}`
        : 'Draft soon';
    }
    case 'trade-offer':
      return 'Trade offer';
    case 'result':
      if (c.second !== null) {
        const won = Number(c.won) + Number(c.second.won);
        const tied = Number(c.tied) + Number(c.second.tied);
        // The two games' record, said as theirs: a median game is a third.
        return `Two games: ${recordText({ wins: won, losses: 2 - won - tied, ties: tied })}`;
      }
      return c.tied ? 'A tie' : c.won ? 'You won' : 'You lost';
    case 'waiver-pending':
      // WITH WHEN THEY ARE DECIDED, as B-445 has it (the audit of
      // 2026-10-20: the card said only how many).
      return `${c.count} claim${c.count === 1 ? '' : 's'}, decided ${when(Date.parse(c.processesAt), zone)}`;
  }
}

function choreTone(c: Chore): Tone {
  switch (c.kind) {
    case 'lineup':
      return 'warn';
    case 'draft':
      return c.roomOpen ? 'good' : 'neutral';
    case 'trade-offer':
      return 'accent';
    case 'result':
      return c.won && (c.second === null || c.second.won) ? 'good' : 'neutral';
    case 'waiver-pending':
      return 'neutral';
  }
}

/** A draft's time on the hub, in the reader's zone and named (B-180, B-181). */
function when(ms: number, zone: string): string {
  return formatInZone(ms, zone, { weekday: 'short', hour: 'numeric', minute: '2-digit' });
}

function opensIn(at: number, now: number): string {
  const ms = at - now;
  if (ms <= 0) return 'Opening now';
  const h = Math.floor(ms / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  if (h >= 48) return `In ${Math.round(h / 24)} days`;
  if (h >= 1) return `Opens in ${h}h ${m}m`;
  return `Opens in ${m}m`;
}
